/**
 * WIL-207 — one vehicle-selector binding for hero, header drawer, PLP, 404.
 * Cascade + persist + navigate + anti-redirect handshake (contract §7).
 */
(function () {
  const SELECTED_KEY = 'selectedVehicle';
  const FILTER_BY_DISMISSED_KEY = 'mapFilterByDismissed';

  /** @type {Window & { MAPVehicleData?: { loadCatalog: () => Promise<unknown>; getMakes: Function; getModels: Function; getYears: Function; getSeries: Function; vehicleShopUrl: (v: Record<string, string | undefined>) => string }; MAPGarage?: { writeSelected?: (v: Record<string, string | undefined> | null) => void; vehicleShopUrl?: (v: Record<string, string | undefined>) => string }; MapToast?: { show?: (msg: string) => void } }} */
  const win = window;

  /** @type {unknown} */
  let catalogData = null;

  /** @returns {Record<string, string | undefined> | null} */
  function readSelected() {
    try {
      const parsed = JSON.parse(window.localStorage.getItem(SELECTED_KEY) || 'null');
      return parsed && typeof parsed === 'object' ? parsed : null;
    } catch {
      return null;
    }
  }

  /** @param {Record<string, string | undefined> | null} vehicle */
  function writeSelected(vehicle) {
    if (typeof win.MAPGarage?.writeSelected === 'function') {
      win.MAPGarage.writeSelected(vehicle);
      return;
    }
    if (vehicle) {
      window.localStorage.setItem(SELECTED_KEY, JSON.stringify(vehicle));
    } else {
      window.localStorage.removeItem(SELECTED_KEY);
    }
    window.dispatchEvent(new CustomEvent('map:vehicle-selected', { detail: vehicle }));
  }

  /**
   * @param {HTMLSelectElement} select
   * @param {string[]} values
   * @param {string} selected
   */
  function fillSelect(select, values, selected) {
    const placeholder = select.querySelector('option[value=""]');
    select.replaceChildren();
    if (placeholder) {
      placeholder.selected = false;
      placeholder.removeAttribute('selected');
      select.appendChild(placeholder);
    }
    else {
      const empty = document.createElement('option');
      empty.value = '';
      empty.disabled = true;
      empty.textContent = select.getAttribute('aria-label') || '';
      select.appendChild(empty);
    }

    values.forEach((value) => {
      const option = document.createElement('option');
      option.value = value;
      option.textContent = value;
      if (value === selected) option.selected = true;
      select.appendChild(option);
    });

    if (selected && values.includes(selected)) select.value = selected;
    else select.selectedIndex = 0;
  }

  /** @param {HTMLSelectElement} select */
  function resetSelect(select) {
    const placeholder = select.querySelector('option[value=""]');
    select.replaceChildren();
    if (placeholder) select.appendChild(placeholder);
    select.selectedIndex = 0;
    select.disabled = true;
  }

  /** @param {HTMLFormElement} form */
  function readVehicle(form) {
    const make = form.querySelector('[data-vehicle-make]');
    const model = form.querySelector('[data-vehicle-model]');
    const year = form.querySelector('[data-vehicle-year]');
    const series = form.querySelector('[data-vehicle-series]');
    return {
      make: make instanceof HTMLSelectElement ? make.value.trim() : '',
      model: model instanceof HTMLSelectElement ? model.value.trim() : '',
      year: year instanceof HTMLSelectElement ? year.value.trim() : '',
      series: series instanceof HTMLSelectElement ? series.value.trim() : '',
    };
  }

  /** @param {Record<string, string>} vehicle */
  function shopUrl(vehicle) {
    const builder = win.MAPVehicleData?.vehicleShopUrl || win.MAPGarage?.vehicleShopUrl;
    if (typeof builder === 'function') return builder(vehicle);
    return '/collections/all';
  }

  /**
   * @param {Record<string, string>} vehicle
   * @param {string} keyword
   */
  function keywordSearchUrl(vehicle, keyword) {
    const parts = [vehicle.make, vehicle.model, vehicle.year, keyword].filter(Boolean);
    return `/search?type=product&q=${encodeURIComponent(parts.join(' '))}`;
  }

  /** @param {HTMLFormElement} form */
  function syncFindSubmit(form) {
    const panel = form.closest('[data-vehicle-find-panel]');
    if (!panel) return;
    const submit = panel.querySelector('[data-vehicle-find-submit]');
    if (!(submit instanceof HTMLButtonElement)) return;
    const vehicle = readVehicle(form);
    submit.disabled = !vehicle.make;
  }

  /** @param {Record<string, string | undefined> | null | undefined} vehicle */
  function vehicleLabel(vehicle) {
    if (!vehicle?.make) return '';
    return [vehicle.make, vehicle.model, vehicle.year, vehicle.series].filter(Boolean).join(' ');
  }

  /** Header / Filtering-by pill — Figma 3641:709 “Toyota Corolla 2008”. */
  function pillLabel(vehicle) {
    if (!vehicle?.make) return '';
    return [vehicle.make, vehicle.model, vehicle.year].filter(Boolean).join(' ');
  }

  /**
   * URL vehicle always wins. Drawers + home hero restore the garage
   * vehicle (Figma selected pill + filled cascade). 404 stays a fresh search.
   * @param {HTMLFormElement} form
   */
  function restoreSource(form) {
    const fromUrl = readVehicleFromPath(window.location.pathname);
    if (fromUrl) return fromUrl;
    if (
      form.hasAttribute('data-vehicle-persist') ||
      form.closest('[data-vehicle-find-panel]') ||
      form.closest('.home-hero__search-panel')
    ) {
      return readSelected() || {};
    }
    return {};
  }

  /**
   * @param {HTMLFormElement} form
   * @param {Record<string, string | undefined>} saved
   */
  function applyVehicleToForm(form, saved) {
    const adapter = win.MAPVehicleData;
    const data = catalogData;
    const make = form.querySelector('[data-vehicle-make]');
    const model = form.querySelector('[data-vehicle-model]');
    const year = form.querySelector('[data-vehicle-year]');
    const series = form.querySelector('[data-vehicle-series]');
    if (
      !adapter ||
      !data ||
      !(make instanceof HTMLSelectElement) ||
      !(model instanceof HTMLSelectElement) ||
      !(year instanceof HTMLSelectElement) ||
      !(series instanceof HTMLSelectElement)
    ) {
      return;
    }

    fillSelect(make, adapter.getMakes(data), saved.make || '');
    make.disabled = false;

    if (saved.make) {
      fillSelect(model, adapter.getModels(data, saved.make), saved.model || '');
      model.disabled = false;
    } else {
      resetSelect(model);
    }

    if (saved.make && saved.model) {
      fillSelect(year, adapter.getYears(data, saved.make, saved.model), saved.year || '');
      year.disabled = false;
    } else {
      resetSelect(year);
    }

    if (saved.make && saved.model && saved.year) {
      fillSelect(series, adapter.getSeries(data, saved.make, saved.model, saved.year), saved.series || '');
      series.disabled = false;
    } else {
      resetSelect(series);
    }

    syncFindSubmit(form);
  }

  function resetAllForms() {
    document.querySelectorAll('[data-vehicle-search]').forEach((form) => {
      if (form instanceof HTMLFormElement) applyVehicleToForm(form, {});
    });
    document.querySelectorAll('[data-vehicle-keyword-form] [name="q"]').forEach((input) => {
      if (input instanceof HTMLInputElement) input.value = '';
    });
  }

  function syncActiveDisplays() {
    const fromUrl = readVehicleFromPath(window.location.pathname);
    const saved = readSelected();
    const active = Boolean(fromUrl?.make || saved?.make);
    const label = vehicleLabel(fromUrl || saved);

    document.querySelectorAll('[data-vehicle-clear]').forEach((button) => {
      if (!(button instanceof HTMLElement)) return;
      // Global / drawer / PLP clears follow saved + URL vehicle. A panel-only
      // reset (if any remain) only shows when that panel's form has a value.
      const panel = button.closest('[data-vehicle-search-panel]');
      let show = active;
      if (panel && !button.hasAttribute('data-vehicle-clear-global')) {
        const form = panel.querySelector('[data-vehicle-search]');
        show = form instanceof HTMLFormElement && Boolean(readVehicle(form).make);
      }
      button.hidden = !show;
      if (button instanceof HTMLButtonElement) button.disabled = !show;
    });

    const shortLabel = pillLabel(fromUrl || saved);

    document.querySelectorAll('[data-vehicle-active]').forEach((chip) => {
      if (!(chip instanceof HTMLElement)) return;
      // PLP toolbar uses Filtering-by or Search-by-vehicle — never both.
      if (chip.classList.contains('collection-page__vehicle-chip')) {
        chip.hidden = true;
        return;
      }
      const text = chip.querySelector('[data-vehicle-active-label]');
      const chipLabel = chip.hasAttribute('data-vehicle-pill-short') ? shortLabel : label;
      if (text) text.textContent = chipLabel;
      chip.hidden = !chipLabel;
    });

    syncHeaderPills(saved);
    syncFilterBy(fromUrl, saved);
  }

  /** @param {Record<string, string | undefined> | null} saved */
  function syncHeaderPills(saved) {
    const label = pillLabel(saved);
    document.querySelectorAll('[data-header-vehicle-pill]').forEach((button) => {
      if (!(button instanceof HTMLElement)) return;
      button.classList.toggle('is-vehicle-set', Boolean(label));
      const idle = button.querySelector('.site-header__vehicle-pill-idle');
      const set = button.querySelector('.site-header__vehicle-pill-set');
      const text = button.querySelector('[data-header-vehicle-label]');
      if (idle instanceof HTMLElement) idle.hidden = Boolean(label);
      if (set instanceof HTMLElement) set.hidden = !label;
      if (text instanceof HTMLElement) {
        text.textContent = label;
        text.hidden = !label;
      }
      const idleLabel = button.dataset.headerVehicleIdleLabel || '';
      const selectedTemplate = button.dataset.headerVehicleSelectedLabel || '';
      button.setAttribute(
        'aria-label',
        label && selectedTemplate ? selectedTemplate.replace('__VEHICLE__', label) : idleLabel
      );
    });

    const wrap = document.querySelector('.site-header__search-wrap');
    const defaultPill = document.querySelector('.site-header__search-wrap > [data-header-vehicle-pill]');
    if (wrap instanceof HTMLElement) {
      wrap.classList.toggle('is-vehicle-set', Boolean(label));
      if (defaultPill instanceof HTMLElement && label) {
        wrap.style.setProperty('--header-vehicle-pill-width', `${defaultPill.offsetWidth}px`);
      } else {
        wrap.style.removeProperty('--header-vehicle-pill-width');
      }
    }
  }

  /**
   * @param {Record<string, string | undefined> | null} fromUrl
   * @param {Record<string, string | undefined> | null} saved
   */
  function syncFilterBy(fromUrl, saved) {
    if (fromUrl?.make) {
      try {
        sessionStorage.removeItem(FILTER_BY_DISMISSED_KEY);
      } catch {
        /* private mode */
      }
    }
    let dismissed = false;
    try {
      dismissed = sessionStorage.getItem(FILTER_BY_DISMISSED_KEY) === 'true';
    } catch {
      dismissed = false;
    }
    const filterVehicle = fromUrl?.make
      ? fromUrl
      : saved?.make && !dismissed
        ? saved
        : null;
    const label = pillLabel(filterVehicle);
    const href = filterVehicle ? shopUrl(filterVehicle) : '/collections/all';

    document.querySelectorAll('[data-vehicle-filter-by]').forEach((row) => {
      if (!(row instanceof HTMLElement)) return;
      row.hidden = !label;
      const text = row.querySelector('[data-vehicle-filter-by-label]');
      if (text) text.textContent = label;
      const link = row.querySelector('[data-vehicle-filter-by-shop]');
      if (link instanceof HTMLAnchorElement) link.href = href;
      const clear = row.querySelector('[data-vehicle-clear-filter]');
      if (clear instanceof HTMLElement) clear.hidden = !label;
    });

    document.querySelectorAll('.collection-page__toolbar').forEach((toolbar) => {
      if (!(toolbar instanceof HTMLElement)) return;
      toolbar.classList.toggle('is-filtering-by', Boolean(label));
    });

    // Figma 3823:5449 — Filtering-by + Search-all replaces the Search-by-vehicle box.
    document.querySelectorAll('.collection-page__vehicle-panel').forEach((panel) => {
      if (panel instanceof HTMLElement) panel.hidden = Boolean(label);
    });
  }

  /** @param {string} chunk */
  function decodeChunk(chunk) {
    try {
      return decodeURIComponent(chunk);
    } catch {
      return chunk;
    }
  }

  /** @param {string} pathname */
  function collectionPathWithoutVehicle(pathname) {
    const parts = pathname.split('/');
    if (parts[1] !== 'collections' || !parts[2]) return pathname;
    const handle = parts[2];
    const kept = (parts[3] || '')
      .split('+')
      .filter((chunk) => {
        const decoded = decodeChunk(chunk);
        return (
          decoded &&
          decoded.indexOf('make_') !== 0 &&
          decoded.indexOf('model_') !== 0 &&
          decoded.indexOf('year_') !== 0 &&
          decoded.indexOf('series_') !== 0
        );
      });
    return kept.length ? `/collections/${handle}/${kept.join('+')}` : `/collections/${handle}`;
  }

  function clearVehicle() {
    writeSelected(null);
    resetAllForms();
    syncActiveDisplays();

    const pathname = window.location.pathname;
    if (pathHasVehicleFilters(pathname)) {
      window.location.assign(`${collectionPathWithoutVehicle(pathname)}${window.location.search}`);
    }
  }

  /** @param {HTMLFormElement} form */
  async function bindForm(form) {
    if (form.dataset.vehicleSelectorInit === 'true') return;
    form.dataset.vehicleSelectorInit = 'true';

    const adapter = win.MAPVehicleData;
    if (!adapter) return;

    const make = form.querySelector('[data-vehicle-make]');
    const model = form.querySelector('[data-vehicle-model]');
    const year = form.querySelector('[data-vehicle-year]');
    const series = form.querySelector('[data-vehicle-series]');
    if (
      !(make instanceof HTMLSelectElement) ||
      !(model instanceof HTMLSelectElement) ||
      !(year instanceof HTMLSelectElement) ||
      !(series instanceof HTMLSelectElement)
    ) {
      return;
    }

    let data;
    try {
      data = await adapter.loadCatalog();
    } catch (error) {
      console.warn('vehicle catalog failed to load', error);
      win.MapToast?.show?.('Could not load vehicle list. Please try again.');
      return;
    }

    catalogData = data;
    applyVehicleToForm(form, restoreSource(form));
    syncActiveDisplays();

    make.addEventListener('change', () => {
      resetSelect(year);
      resetSelect(series);
      if (!make.value) {
        resetSelect(model);
        syncFindSubmit(form);
        syncActiveDisplays();
        return;
      }
      fillSelect(model, adapter.getModels(data, make.value), '');
      model.disabled = false;
      syncFindSubmit(form);
      syncActiveDisplays();
    });

    model.addEventListener('change', () => {
      resetSelect(series);
      if (!model.value) {
        resetSelect(year);
        syncActiveDisplays();
        return;
      }
      fillSelect(year, adapter.getYears(data, make.value, model.value), '');
      year.disabled = false;
      syncActiveDisplays();
    });

    year.addEventListener('change', () => {
      if (!year.value) {
        resetSelect(series);
        syncActiveDisplays();
        return;
      }
      const options = adapter.getSeries(data, make.value, model.value, year.value);
      if (options.length) {
        fillSelect(series, options, '');
        series.disabled = false;
      } else {
        resetSelect(series);
      }
      syncActiveDisplays();
    });

    series.addEventListener('change', () => {
      syncActiveDisplays();
    });

    form.addEventListener('submit', (event) => {
      event.preventDefault();
      const vehicle = readVehicle(form);
      if (!vehicle.make) return;
      writeSelected(vehicle);

      const dialog = form.closest('dialog');
      if (dialog instanceof HTMLDialogElement && dialog.open) {
        if (window.MapUI?.closeDrawer) window.MapUI.closeDrawer(dialog);
        else dialog.close();
      }

      if (document.querySelector('[data-product-page]')) return;

      if (document.querySelector('[data-collection-page]')) {
        const handle = collectionHandleFromPath(window.location.pathname) || 'all';
        window.location.href = collectionVehicleUrl(handle, vehicle);
        return;
      }

      window.location.href = shopUrl(vehicle);
    });
  }

  /** @param {HTMLFormElement} form */
  function bindKeywordForm(form) {
    if (form.dataset.vehicleKeywordInit === 'true') return;
    form.dataset.vehicleKeywordInit = 'true';

    form.addEventListener('submit', (event) => {
      const panel = form.closest('[data-vehicle-search-panel]');
      const vehicleForm = panel?.querySelector('[data-vehicle-search]');
      const query = form.querySelector('[name="q"]');
      const keyword = query instanceof HTMLInputElement ? query.value.trim() : '';
      const vehicle =
        vehicleForm instanceof HTMLFormElement ? readVehicle(vehicleForm) : { make: '', model: '', year: '', series: '' };

      if (!vehicle.make && !keyword) return;

      event.preventDefault();
      if (vehicle.make) writeSelected(vehicle);

      if (keyword) {
        window.location.href = keywordSearchUrl(vehicle, keyword);
        return;
      }
      window.location.href = shopUrl(vehicle);
    });
  }

  /** @param {string} pathname */
  function pathHasVehicleFilters(pathname) {
    return (
      pathname.includes('+make_') ||
      pathname.includes('+model_') ||
      pathname.includes('+year_') ||
      pathname.includes('+series_') ||
      pathname.includes('/make_') ||
      pathname.includes('/model_') ||
      pathname.includes('/year_') ||
      pathname.includes('/series_')
    );
  }

  /** @param {string} pathname */
  function readVehicleFromPath(pathname) {
    const parts = pathname.split('/');
    if (parts[1] !== 'collections' || parts.length < 4) return null;
    /** @type {Record<string, string>} */
    const vehicle = { make: '', model: '', year: '', series: '' };
    (parts[3] || '').split('+').forEach((chunk) => {
      let decoded = chunk;
      try {
        decoded = decodeURIComponent(chunk);
      } catch {
        /* keep raw */
      }
      if (decoded.indexOf('make_') === 0) vehicle.make = decoded.slice(5);
      else if (decoded.indexOf('model_') === 0) vehicle.model = decoded.slice(6);
      else if (decoded.indexOf('year_') === 0) vehicle.year = decoded.slice(5);
      else if (decoded.indexOf('series_') === 0) vehicle.series = decoded.slice(7);
    });
    return vehicle.make ? vehicle : null;
  }

  /** @param {string} pathname */
  function collectionHandleFromPath(pathname) {
    const parts = pathname.split('/');
    return parts[1] === 'collections' && parts[2] ? parts[2] : '';
  }

  /** @param {string} pathname */
  function hasLegacyCategoryPathFilters(pathname) {
    const match = pathname.match(/\/collections\/[^/]+\/(.*)/);
    if (!match || !match[1]) return false;
    return match[1].split('+').some((chunk) => {
      return chunk.indexOf('categorylevel') !== -1 || chunk.indexOf('Part Classification:') !== -1;
    });
  }

  function recentlyAppliedCategoryFilter() {
    const raw = sessionStorage.getItem('categoryFilterApplied');
    if (!raw) return false;
    const stamp = Number.parseInt(raw, 10);
    return Number.isFinite(stamp) && Date.now() - stamp < 2000;
  }

  /**
   * @param {string} handle
   * @param {Record<string, string | undefined>} vehicle
   */
  function collectionVehicleUrl(handle, vehicle) {
    const segments = [];
    if (vehicle.make) segments.push(`+make_${encodeURIComponent(String(vehicle.make).trim())}`);
    if (vehicle.model) segments.push(`+model_${encodeURIComponent(String(vehicle.model).trim())}`);
    if (vehicle.year) segments.push(`+year_${encodeURIComponent(String(vehicle.year).trim())}`);
    if (vehicle.series) segments.push(`+series_${encodeURIComponent(String(vehicle.series).trim())}`);
    return `/collections/${handle}/${segments.join('')}${window.location.search}`;
  }

  /**
   * Contract §7: if a vehicle is selected, unfiltered collection pages redirect
   * onto the vehicle path — unless a category filter just applied (exact keys
   * written by collection-filters.js). Consume + clear `justFiltered`.
   */
  function applyVehicleHandshake() {
    if (!document.querySelector('[data-collection-page]')) return;
    if (document.documentElement.dataset.vehicleHandshake === 'true') return;
    document.documentElement.dataset.vehicleHandshake = 'true';

    const pathname = window.location.pathname;
    const fromUrl = readVehicleFromPath(pathname);
    if (fromUrl) {
      writeSelected(fromUrl);
      sessionStorage.removeItem('justFiltered');
      return;
    }

    const saved = readSelected();
    if (!saved?.make) return;

    const skip =
      sessionStorage.getItem('justFiltered') === 'true' ||
      recentlyAppliedCategoryFilter() ||
      pathHasVehicleFilters(pathname) ||
      hasLegacyCategoryPathFilters(pathname);

    if (skip) {
      sessionStorage.removeItem('justFiltered');
      return;
    }

    const handle = collectionHandleFromPath(pathname);
    if (!handle) return;
    window.location.replace(collectionVehicleUrl(handle, saved));
  }

  function bindFindPanelSubmit() {
    document.querySelectorAll('[data-vehicle-find-submit]').forEach((button) => {
      if (!(button instanceof HTMLButtonElement) || button.dataset.vehicleFindSubmitBound === 'true') return;
      button.dataset.vehicleFindSubmitBound = 'true';
      button.addEventListener('click', () => {
        const panel = button.closest('[data-vehicle-find-panel]');
        const form = panel?.querySelector('[data-vehicle-search]');
        if (form instanceof HTMLFormElement) form.requestSubmit();
      });
    });
  }

  function bindClearButtons() {
    document.querySelectorAll('[data-vehicle-clear]').forEach((button) => {
      if (!(button instanceof HTMLElement) || button.dataset.vehicleClearBound === 'true') return;
      button.dataset.vehicleClearBound = 'true';
      button.addEventListener('click', (event) => {
        event.preventDefault();

        if (button.hasAttribute('data-vehicle-clear-global')) {
          clearVehicle();
          return;
        }

        // Hero keyword-only reset — garage / header pill stay.
        const panel = button.closest('[data-vehicle-search-panel]');
        if (panel) {
          const form = panel.querySelector('[data-vehicle-search]');
          if (form instanceof HTMLFormElement) applyVehicleToForm(form, {});
          const keyword = panel.querySelector('[data-vehicle-keyword-form] [name="q"]');
          if (keyword instanceof HTMLInputElement) keyword.value = '';
          syncActiveDisplays();
          return;
        }

        clearVehicle();
      });
    });

    document.querySelectorAll('[data-vehicle-clear-filter]').forEach((button) => {
      if (!(button instanceof HTMLElement) || button.dataset.vehicleClearFilterBound === 'true') return;
      button.dataset.vehicleClearFilterBound = 'true';
      button.addEventListener('click', (event) => {
        event.preventDefault();
        clearVehicleFilter();
      });
    });
  }

  /**
   * Filtering-by ✕ — deselect the garage vehicle and leave /+make_… in one
   * hop (WIL-457). Keeping the vehicle selected produced a third screen
   * (unfiltered hub + “does not fit”) before a second ✕ cleared it.
   */
  function clearVehicleFilter() {
    try {
      sessionStorage.setItem('justFiltered', 'true');
      sessionStorage.setItem('categoryFilterApplied', Date.now().toString());
      sessionStorage.removeItem(FILTER_BY_DISMISSED_KEY);
    } catch {
      /* private mode */
    }
    clearVehicle();
  }

  function boot() {
    applyVehicleHandshake();
    document.querySelectorAll('[data-vehicle-search]').forEach((form) => {
      if (form instanceof HTMLFormElement) bindForm(form);
    });
    document.querySelectorAll('[data-vehicle-keyword-form]').forEach((form) => {
      if (form instanceof HTMLFormElement) bindKeywordForm(form);
    });
    bindFindPanelSubmit();
    bindClearButtons();
    syncActiveDisplays();
  }

  /**
   * Garage Select / drawer save re-apply: refill persist, find-panel, and
   * home-hero cascades from selectedVehicle. 404 stays a fresh search.
   * @param {Record<string, string | undefined>} vehicle
   */
  function applySelectedToPersistForms(vehicle) {
    if (!catalogData || !vehicle?.make) return;
    document.querySelectorAll('[data-vehicle-search]').forEach((form) => {
      if (!(form instanceof HTMLFormElement)) return;
      if (
        !form.hasAttribute('data-vehicle-persist') &&
        !form.closest('[data-vehicle-find-panel]') &&
        !form.closest('.home-hero__search-panel')
      ) {
        return;
      }
      applyVehicleToForm(form, vehicle);
    });
  }

  window.addEventListener('map:vehicle-selected', (event) => {
    if (event instanceof CustomEvent && event.detail) {
      try {
        sessionStorage.removeItem(FILTER_BY_DISMISSED_KEY);
      } catch {
        /* private mode */
      }
      applySelectedToPersistForms(event.detail);
      syncActiveDisplays();
      return;
    }
    resetAllForms();
    syncActiveDisplays();
  });

  window.addEventListener('storage', (event) => {
    if (event.key !== 'selectedVehicle') return;
    const vehicle = readSelected();
    if (vehicle?.make) applySelectedToPersistForms(vehicle);
    else resetAllForms();
    syncActiveDisplays();
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', boot);
})();
